import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Spin, 
  Typography, 
  Button, 
  Space, 
  Dropdown, 
  Menu,
  Anchor,
  Empty
} from 'antd'
import { 
  LeftOutlined, 
  RightOutlined, 
  MenuOutlined,
  BookOutlined,
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined,
  DownOutlined
} from '@ant-design/icons'
import { useParams, useNavigate } from 'react-router-dom'
import './BookReader.css'

const { Title, Text } = Typography
const { Link } = Anchor

function BookReader() {
  const { bookId, pageNumber = '1' } = useParams()
  const navigate = useNavigate()
  
  // 书籍数据状态
  const [book, setBook] = useState(null)
  const [chapters, setChapters] = useState([])
  const [currentPage, setCurrentPage] = useState(parseInt(pageNumber) || 1)
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(false)
  const [totalPages, setTotalPages] = useState(0)
  
  // 章节导航状态
  const [showChapterNav, setShowChapterNav] = useState(false)
  
  // 获取书籍信息
  useEffect(() => {
    if (bookId) {
      fetchBookInfo()
    }
  }, [bookId])
  
  // 当页码变化时获取页面内容
  useEffect(() => {
    if (bookId && currentPage) {
      fetchPageContent()
      // 更新URL
      navigate(`/books/${bookId}/page/${currentPage}`, { replace: true })
    }
  }, [bookId, currentPage])
  
  const fetchBookInfo = async () => {
    setLoading(true)
    
    try {
      // 获取书籍基本信息和章节
      const response = await fetch(`/api/books/${bookId}`)
      const data = await response.json()
      
      if (response.ok) {
        setBook(data.book)
        setChapters(data.chapters || [])
        setTotalPages(data.book?.page_count || 0)
      }
    } catch (error) {
      console.error('获取书籍信息失败:', error)
    } finally {
      setLoading(false)
    }
  }
  
  const fetchPageContent = async () => {
    setLoading(true)
    
    try {
      const response = await fetch(`/api/books/${bookId}/content/${currentPage}`)
      const data = await response.json()
      
      if (response.ok) {
        setContent(data.content || '')
        setTotalPages(data.totalPages || totalPages)
      }
    } catch (error) {
      console.error('获取页面内容失败:', error)
    } finally {
      setLoading(false)
    }
  }
  
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
    }
  }
  
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1)
    }
  }
  
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1)
    }
  }
  
  // 跳转到指定章节
  const goToChapter = (chapter) => {
    if (chapter.page_number) {
      handlePageChange(chapter.page_number)
      setShowChapterNav(false)
    }
  }
  
  // 章节导航菜单
  const renderChapterMenu = () => {
    if (chapters.length === 0) {
      return (
        <Menu>
          <Menu.Item key="no-chapters" disabled>
            暂无章节信息
          </Menu.Item>
        </Menu>
      )
    }
    
    // 构建章节树
    const buildChapterTree = (chapters) => {
      const chapterMap = new Map()
      const rootChapters = []
      
      // 初始化章节映射
      chapters.forEach(chapter => {
        chapterMap.set(chapter.id, {
          ...chapter,
          children: []
        })
      })
      
      // 构建父子关系
      chapters.forEach(chapter => {
        if (chapter.parent_id === null) {
          rootChapters.push(chapterMap.get(chapter.id))
        } else {
          const parent = chapterMap.get(chapter.parent_id)
          if (parent) {
            parent.children.push(chapterMap.get(chapter.id))
          }
        }
      })
      
      return rootChapters
    }
    
    const chapterTree = buildChapterTree(chapters)
    
    const renderChapterItems = (chapters, level = 0) => {
      return chapters.map(chapter => {
        const paddingLeft = `${level * 16 + 8}px`
        
        if (chapter.children && chapter.children.length > 0) {
          return (
            <Menu.SubMenu
              key={`chapter-${chapter.id}`}
              title={
                <span 
                  style={{ paddingLeft }}
                  className="chapter-menu-item"
                >
                  {chapter.title}
                </span>
              }
            >
              {renderChapterItems(chapter.children, level + 1)}
            </Menu.SubMenu>
          )
        } else {
          return (
            <Menu.Item
              key={`chapter-${chapter.id}`}
              onClick={() => goToChapter(chapter)}
            >
              <span 
                style={{ paddingLeft }}
                className="chapter-menu-item"
              >
                {chapter.title}
              </span>
            </Menu.Item>
          )
        }
      })
    }
    
    return (
      <Menu className="chapter-navigation-menu">
        {renderChapterItems(chapterTree)}
      </Menu>
    )
  }
  
  // 锚点导航（页面内章节）
  const renderAnchorNav = () => {
    // 这里可以根据页面内容提取锚点
    // 暂时返回空，后续可以实现
    return null
  }
  
  if (loading && !book) {
    return (
      <div className="book-reader-page">
        <div className="loading-container">
          <Spin size="large" />
          <Text type="secondary">正在加载书籍信息...</Text>
        </div>
      </div>
    )
  }
  
  if (!book) {
    return (
      <div className="book-reader-page">
        <div className="empty-container">
          <Empty description="书籍不存在或加载失败" />
          <Button 
            type="primary" 
            onClick={() => navigate('/books')}
          >
            返回书库
          </Button>
        </div>
      </div>
    )
  }
  
  return (
    <div className="book-reader-page">
      <div className="book-reader-container">
        {/* 书籍头部信息 */}
        <Card className="book-header-card">
          <div className="book-header">
            <div className="book-info">
              <Title level={3} className="book-title">
                <BookOutlined /> {book.title}
              </Title>
              <Space className="book-meta">
                {book.author && (
                  <Text>
                    <UserOutlined /> {book.author}
                  </Text>
                )}
                {book.dynasty && (
                  <Text>
                    <CalendarOutlined /> {book.dynasty}
                  </Text>
                )}
                <Text>
                  <FileTextOutlined /> {totalPages} 页
                </Text>
              </Space>
            </div>
            
            <div className="navigation-controls">
              <Space>
                <Button
                  onClick={() => navigate('/books')}
                  className="back-button"
                >
                  返回书库
                </Button>
                
                {chapters.length > 0 && (
                  <Dropdown
                    overlay={renderChapterMenu()}
                    visible={showChapterNav}
                    onVisibleChange={setShowChapterNav}
                    trigger={['click']}
                  >
                    <Button className="chapter-nav-button">
                      章节导航 <DownOutlined />
                    </Button>
                  </Dropdown>
                )}
              </Space>
            </div>
          </div>
        </Card>
        
        {/* 阅读内容区域 */}
        <div className="reader-content">
          <Spin spinning={loading && !!content}>
            <Card className="content-card">
              {/* 页面导航 */}
              <div className="page-navigation">
                <Button
                  type="primary"
                  icon={<LeftOutlined />}
                  onClick={goToPreviousPage}
                  disabled={currentPage <= 1}
                  className="nav-button prev-button"
                >
                  上一页
                </Button>
                
                <div className="page-info">
                  <Text strong>
                    第 {currentPage} 页 / 共 {totalPages} 页
                  </Text>
                </div>
                
                <Button
                  type="primary"
                  icon={<RightOutlined />}
                  iconPosition="end"
                  onClick={goToNextPage}
                  disabled={currentPage >= totalPages}
                  className="nav-button next-button"
                >
                  下一页
                </Button>
              </div>
              
              {/* 书籍内容 */}
              <div className="book-content">
                {content ? (
                  <div 
                    className="content-text ancient-text"
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                ) : (
                  <Empty description="暂无内容" />
                )}
              </div>
              
              {/* 底部分页导航 */}
              <div className="page-navigation bottom">
                <Button
                  type="primary"
                  icon={<LeftOutlined />}
                  onClick={goToPreviousPage}
                  disabled={currentPage <= 1}
                  className="nav-button prev-button"
                >
                  上一页
                </Button>
                
                <div className="page-info">
                  <Text strong>
                    第 {currentPage} 页 / 共 {totalPages} 页
                  </Text>
                </div>
                
                <Button
                  type="primary"
                  icon={<RightOutlined />}
                  iconPosition="end"
                  onClick={goToNextPage}
                  disabled={currentPage >= totalPages}
                  className="nav-button next-button"
                >
                  下一页
                </Button>
              </div>
            </Card>
          </Spin>
        </div>
      </div>
    </div>
  )
}

export default BookReader